import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LabelChip } from './LabelChip';

const STATUS_ICONS = {
  todo: '\u25CB', // ○
  in_progress: '\u25D0', // ◐
  completed: '\u25CF' // ●
};

const TYPE_ICONS = {
  todo: '\u26A1', // ⚡
  idea: '\u{1F4A1}', // 💡 idea card (series-planning-cards; was 'snippet')
  snippet: '\u{1F4DD}' // 📝 legacy alias (pre-123 rows)
};

const TYPE_TITLES = {
  todo: 'To Do',
  idea: 'Idea'
};

const ACTION_LABELS = {
  todo: { label: '\u25B6 Start', action: 'start' },
  in_progress: { label: '\u2197 Resume', action: 'resume' },
  completed: { label: '\u21BB Revisit', action: 'revisit' }
};

export function CaptureItem({
  capture,
  onStatusChange,
  onEdit,
  onDelete,
  onAction,
  onLabelClick
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(capture.content);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: capture.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const handleStatusClick = () => {
    // Toggle between todo and completed
    const newStatus = capture.status === 'completed' ? 'todo' : 'completed';
    onStatusChange(capture.id, newStatus);
  };

  const handleEditSubmit = () => {
    if (editContent.trim() && editContent !== capture.content) {
      onEdit(capture.id, editContent.trim());
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleEditSubmit();
    } else if (e.key === 'Escape') {
      setEditContent(capture.content);
      setIsEditing(false);
    }
  };

  const captureType = capture.entry_type || 'todo';
  const isTodo = captureType === 'todo';
  const actionConfig = ACTION_LABELS[capture.status];
  const labels = Array.isArray(capture.labels) ? capture.labels : [];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`capture-item capture-item--${capture.status || 'none'} capture-item--${captureType}`}
    >
      {/* Type Icon */}
      <span
        className={`capture-item__type-icon capture-item__type-icon--${captureType}`}
        title={TYPE_TITLES[captureType] || 'Snippet'}
      >
        {TYPE_ICONS[captureType]}
      </span>

      {/* Drag Handle */}
      <span className="capture-item__drag-handle" {...attributes} {...listeners}>
        <span className="capture-item__drag-dots"></span>
      </span>

      {/* Status Icon — To Dos only; Snippets carry no status */}
      {isTodo && capture.status && (
        <button
          className={`capture-item__status capture-item__status--${capture.status}`}
          onClick={handleStatusClick}
          title={`Status: ${capture.status.replace('_', ' ')}`}
        >
          {STATUS_ICONS[capture.status]}
        </button>
      )}

      {/* Content */}
      <div className="capture-item__content-wrapper">
        {isEditing ? (
          <input
            type="text"
            className="capture-item__edit-input"
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            onBlur={handleEditSubmit}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        ) : (
          <>
            <span
              className="capture-item__content"
              onDoubleClick={() => setIsEditing(true)}
            >
              {capture.content}
            </span>
            {/* Labels */}
            {labels.length > 0 && (
              <div className="capture-item__labels">
                {labels.map(label => (
                  <LabelChip
                    key={label.id}
                    label={label}
                    onClick={onLabelClick}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Spawn / action button — To Dos show status-driven Start/Resume/Revisit;
          Snippets get a plain Start (spawn is available on both tabs). */}
      {isTodo && actionConfig && (
        <button
          className="capture-item__action"
          onClick={() => onAction(capture, actionConfig.action)}
        >
          {actionConfig.label}
        </button>
      )}
      {!isTodo && (
        <button
          className="capture-item__action"
          onClick={() => onAction(capture, 'start')}
        >
          {'▶ Start'}
        </button>
      )}

      {/* Delete Button */}
      <button
        className="capture-item__delete"
        onClick={() => onDelete(capture.id)}
        title="Delete capture"
      >
        X
      </button>
    </div>
  );
}

export default CaptureItem;
